import {
  BreadcrumbType,
  DynamicUiRefProps,
  Form,
  FormField,
  FormSubmitHandler,
  Separator,
  showNotification,
} from 'platform/components';
import {Box, Heading, HStack, Show, VStack} from 'platform/foundation';

import {useRef} from 'react';

import {isNotNil, omit} from 'ramda';

import {previewCustomerMessagesSchema, useGetCustomerMessageDefinitionQuery} from '@dms/api/core';
import {
  useGetEffectiveValueWithoutReferenceQuery,
  useSetTenantSettingValueMutation,
} from '@dms/api/settings';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsFooter, SettingsTemplate} from '@dms/settings/shared';
import {getAvailableLocales, handleApiError, useTenant} from '@dms/shared';

import {useRequiredParams} from 'shared/web';

import {AdditionalSettings} from './components/AdditionalSettings';
import {EmailChannelControl} from './components/EmailChannelControl';
import {MessageSpecificRequirements} from './components/MessageSpecificRequirements';
import {SettingsChecklist} from './components/SettingsChecklist';
import {SmsChannelControl} from './components/SmsChannelControl';
import {SmtpCredentials} from './components/SmtpCredentials';
import {parseAutomaticMessageSettingsValue} from './types/AutomaticMessageSettingsValue';
import {createFormValuesSchema, dynamicUiDefaultValuesKeys, FormValues} from './types/FormValues';
import {buildContextTemplatesRecord} from './utils/buildContextTemplatesRecord';
import {buildContextTemplatesRequest} from './utils/buildContextTemplatesRequest';

const SETTINGS_SUFFIX = '-setting';

const breadcrumbs: BreadcrumbType[] = [
  {
    label: i18n.t('page.settings.labels.scheduledMessages'),
    href: settingsRoutes.scheduledMessages,
  },
];

export function ScheduledMessagesDetail() {
  const {id: documentCode} = useRequiredParams();
  const dynamicUiRef = useRef<DynamicUiRefProps>(null);
  const hasValidAdditionalSettingsSubmit = useRef<boolean>(false);
  const previewCustomerMessage = previewCustomerMessagesSchema.safeParse(documentCode);

  const [updateGeneralSettings] = useSetTenantSettingValueMutation();

  const {data: definition, isLoading: isLoadingDefinition} = useGetCustomerMessageDefinitionQuery({
    id: documentCode,
  });

  const hasSchema = isNotNil(definition?.hasSettings);

  const {data: generalSettings, isLoading: isLoadingGeneralSettings} =
    useGetEffectiveValueWithoutReferenceQuery({
      settingId: `${documentCode}${SETTINGS_SUFFIX}`,
    });

  const {tenant, isLoading: isLoadingTenant, isError: isTenantError} = useTenant();

  const settingsValue = parseAutomaticMessageSettingsValue(generalSettings);
  const locales = getAvailableLocales(tenant);

  const formValuesSchema = createFormValuesSchema(locales);
  const templateRecords = buildContextTemplatesRecord(locales, settingsValue);

  const handleSubmit: FormSubmitHandler<FormValues> = async (
    values,
    _setErrors,
    reset,
    blockerContext
  ) => {
    hasValidAdditionalSettingsSubmit.current = false;
    // submit is typed () => void but is async: await waits for trigger() and onSubmit.
    // If submit ever becomes synchronous, schema validation can fail open; the page spec guards this.
    await dynamicUiRef.current?.submit();
    if (isNotNil(dynamicUiRef.current) && !hasValidAdditionalSettingsSubmit.current) {
      blockerContext?.reportApiFailure?.();
      return;
    }

    const {emailSubjects, emailBodies, smsBodies, additionalSettings = {}, ...submitData} = values;

    const contextTemplates = buildContextTemplatesRequest(
      {emailSubjects, emailBodies, smsBodies},
      settingsValue
    );

    await updateGeneralSettings({
      settingId: `${documentCode}${SETTINGS_SUFFIX}`,
      valueRequestBody: {
        force: true,
        // `additionalSettings` comes from a server-driven DynamicUi schema whose key set
        // is not knowable here, so it goes first and the fields this form owns win.
        value: {
          ...additionalSettings,
          ...submitData,
          ...contextTemplates,
        },
      },
    })
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('general.notifications.changesSuccessfullySaved'));
        reset(undefined, {keepValues: true});
      })
      .catch(handleApiError);
  };

  const isLoading = isLoadingGeneralSettings || isLoadingDefinition || isLoadingTenant;
  const dynamicUiDefaultValues = omit(dynamicUiDefaultValuesKeys, generalSettings ?? {});

  return (
    <SettingsTemplate
      header={{
        title: definition?.name ?? i18n.t('page.settings.labels.scheduledMessages'),
        subtitle: definition?.description,
        breadcrumbs,
      }}
      data-testid={testIds.settings.scheduledMessagesDetail('settings-template')}
      isLoading={isLoading}
      isError={isTenantError}
    >
      <Form<FormValues>
        experimentalZodSchema={formValuesSchema}
        defaultValues={{
          enabled: settingsValue.enabled ?? false,
          smsEnabled: settingsValue.smsEnabled ?? false,
          emailEnabled: settingsValue.emailEnabled ?? false,
          emailCredentialsId: settingsValue.emailCredentialsId ?? null,
          ...templateRecords,
          additionalSettings: dynamicUiDefaultValues,
        }}
        onSubmit={handleSubmit}
        shouldWatchForUnsavedChanges
      >
        {(control, formApi, navigateWithoutBlocker) => (
          <HStack spacing={4} align="flex-start">
            <Box maxWidth={200}>
              <VStack spacing={4}>
                <FormField
                  control={control}
                  type="checkbox"
                  name="enabled"
                  label={i18n.t('page.settings.labels.automatizationEnabled')}
                  helperText={i18n.t('page.settings.labels.automatizationEnabledHelperText')}
                />

                <Separator spacing={0} />
                <Heading size={4}>{i18n.t('page.settings.notifications.channels')}</Heading>

                <SmsChannelControl
                  control={control}
                  formApi={formApi}
                  context={definition?.context}
                  locales={locales}
                />

                <EmailChannelControl
                  control={control}
                  formApi={formApi}
                  context={definition?.context}
                  customerMessageId={
                    previewCustomerMessage.success ? previewCustomerMessage.data : null
                  }
                  locales={locales}
                />

                <SmtpCredentials control={control} />

                <Show when={hasSchema}>
                  <AdditionalSettings
                    dynamicUiRef={dynamicUiRef}
                    onValidSubmit={() => {
                      hasValidAdditionalSettingsSubmit.current = true;
                    }}
                    formApi={formApi}
                    control={control}
                    documentCode={documentCode}
                    defaultValues={dynamicUiDefaultValues}
                  />
                </Show>

                <SettingsFooter
                  actions={[
                    {
                      type: 'button',
                      variant: 'secondary',
                      title: i18n.t('general.actions.discardChanges'),
                      onClick: () => navigateWithoutBlocker(settingsRoutes.scheduledMessages),
                    },
                    {
                      type: 'form-button',
                      control,
                      buttonType: 'submit',
                      title: i18n.t('general.actions.save'),
                    },
                  ]}
                />
              </VStack>
            </Box>

            <Box paddingHorizontal={4} maxWidth={200}>
              <VStack spacing={4}>
                <SettingsChecklist
                  customerMessageId={documentCode}
                  control={control}
                  locales={locales}
                  data-testid={testIds.settings.scheduledMessagesDetail('checklist')}
                />

                <MessageSpecificRequirements
                  customerMessageId={documentCode}
                  data-testid={testIds.settings.scheduledMessagesDetail('requirements')}
                />
              </VStack>
            </Box>
          </HStack>
        )}
      </Form>
    </SettingsTemplate>
  );
}
